/**
 * What to paste into an app to reach a model.
 *
 * The whole platform, stated as one screen: nobody should have to work out the
 * machine address, the port, the path shape and the key by reading three
 * documents. They copy a card.
 *
 * The two URL shapes are the reason this is generated rather than written down.
 * A chat client appends `/chat/completions`, so its base ends at `/v1`. The
 * agent's embedder appends `/api/embed` itself, so its base stops at `/ollama`.
 * Swapping them is a hard failure at agent startup with an unhelpful message,
 * so each card names the exact environment variable that takes each value.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError, fetchConnections, testConnection, type Connection, type TestResult } from "./api";
import { ConnectionDetails, useKey } from "./Connection";

/**
 * "Installed" and "Loaded" only describe Ollama.
 *
 * A vLLM model is a process, so it is starting, up, stopped, or dead — and
 * telling someone their model is "Installed" when it crashed twenty minutes ago
 * is the kind of small lie that costs an afternoon. The card still renders, so
 * the URLs and the key stay copyable while it is down.
 */
function StatePill({ conn }: { conn: Connection }) {
  if (conn.state === "loaded") {
    return <span className="pill loaded"><i />{conn.provider === "vllm" ? "Running" : "Loaded"}</span>;
  }
  if (conn.provider !== "vllm") {
    return <span className="pill idle"><i />Installed</span>;
  }
  if (conn.state === "starting") return <span className="pill busy"><i />Starting</span>;
  if (conn.state === "failed") return <span className="pill fail"><i />Failed</span>;
  return <span className="pill idle"><i />Stopped</span>;
}

function Card({ conn }: { conn: Connection }) {
  const [result, setResult] = useState<TestResult | null>(null);

  const probe = useMutation({
    mutationFn: () => testConnection(conn.model),
    onSuccess: setResult,
    onError: (error) =>
      setResult({
        ok: false,
        ms: null,
        detail: error instanceof ApiError ? error.detail : String(error),
      }),
  });

  const keyState = useKey(conn);

  return (
    <div className="conn">
      <header>
        <h3>{conn.model}</h3>
        <span className={`prov ${conn.provider}`}>
          {conn.provider === "ollama" ? "Ollama" : "vLLM"}
        </span>
        <span className="chip">{conn.model_type}</span>
        <StatePill conn={conn} />
      </header>

      <ConnectionDetails conn={conn} keyState={keyState} />

      <div className="foot">
        <button
          className="btn sm"
          type="button"
          onClick={() => probe.mutate()}
          disabled={probe.isPending}
        >
          {probe.isPending ? "Testing…" : "Test connection"}
        </button>
        <button
          className="btn sm"
          type="button"
          onClick={() => {
            if (
              confirm(
                `Reissue the ${conn.provider} key? Every client using the old ` +
                  `one stops working immediately.`,
              )
            ) {
              keyState.rotate.mutate();
            }
          }}
          disabled={keyState.rotate.isPending}
        >
          Reissue key
        </button>
        {result && (
          <span className={result.ok ? "probe ok" : "probe bad"}>{result.detail}</span>
        )}
      </div>
    </div>
  );
}

export default function ConnectionsPage() {
  const connections = useQuery({
    queryKey: ["connections"],
    queryFn: () => fetchConnections(false),
    // Nothing here moves second to second, and each refetch touches Ollama.
  });

  return (
    <main>
      <div className="page-head">
        <div>
          <h1>Connections</h1>
          <p>Everything an app or an agent needs to call a model.</p>
        </div>
      </div>

      {connections.error ? (
        <div className="notice">
          <h3>Cannot list connections</h3>
          <p>
            {connections.error instanceof ApiError
              ? connections.error.detail
              : String(connections.error)}
          </p>
        </div>
      ) : connections.isLoading ? (
        <div className="card">
          <div className="empty">Loading…</div>
        </div>
      ) : connections.data && connections.data.length > 0 ? (
        <div className="conns">
          {connections.data.map((c) => (
            // Provider in the key: an Ollama model and a vLLM alias can share
            // a name, and they are two different cards.
            <Card key={`${c.provider}/${c.model}`} conn={c} />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="empty">
            No models installed yet, so there is nothing to connect to.
          </div>
        </div>
      )}

      <div className="info">
        One key covers every model on Ollama — they share a server and a URL, so
        splitting them would be a rule we invented rather than one the setup
        reflects. Reissuing it affects all of them at once. Each vLLM model has
        its own key, for the same reason in reverse: each one is a separate
        server, so reissuing touches only that model.
      </div>
    </main>
  );
}
